// Drag-and-drop handler factory for BriefEditor's TipTap view.
// Encapsulates parsing of all `application/x-excerptus-*` payload types and
// invoking editor commands for each.

import type { Editor } from "@tiptap/react";
import type { EditorView } from "@tiptap/pm/view";
import {
  buildLibraryQuoteInsert,
  resolveAmazonEligibility,
  type LibraryQuoteSource,
} from "@/lib/composer/libraryQuoteInsert";
import {
  sanitizeExcerptBodyHtml,
  buildExcerptCitationHtml,
  escapeExcerptText,
  type ExcerptCitationParts,
} from "@/lib/composer/excerptDropFormat";
import {
  buildAmazonAffiliateUrl,
  isValidAssociatesTag,
} from "@/lib/amazonAffiliate";

type LibDropPending = { source: LibraryQuoteSource; pos: number; libraryItemId: string | null };

export type ExcerptDropPending = {
  pos: number;
  bodyHtml: string;
  citationParts: ExcerptCitationParts;
  affiliateHref: string;
  libraryItemId: string | null;
  label: string;
};

export type BriefEditorDropOptions = {
  /** Latest editor instance from useEditor(). */
  editorRef: { current: Editor | null };
  /** Latest workspace Amazon Associates tag (for eligibility checks). */
  associatesTagRef: { current: string | null };
  /** Set when a library drop needs the user to confirm an affiliate link. */
  pendingLibDropRef: { current: LibDropPending | null };
  /** Set when an excerpt drop needs the user to confirm an affiliate link. */
  pendingExcerptDropRef: { current: ExcerptDropPending | null };
  setLibDropAnchor: (a: { x: number; y: number } | null) => void;
  setLibDropTitle: (t: string) => void;
  setLibDropOpen: (open: boolean) => void;
  /** Called once a quote from a My Library source lands in the canvas. */
  onSourceInserted?: (libraryItemId: string, label: string) => void;
};

const insertParagraphsFromText = (editor: Editor, pos: number, text: string) => {
  const paragraphs = text.split(/\n{2,}/).map((para) => ({
    type: "paragraph",
    content: [{ type: "text", text: para }],
  }));
  editor.chain().focus().insertContentAt(pos, paragraphs).run();
};

export function buildBriefEditorDropHandler(opts: BriefEditorDropOptions) {
  return (view: EditorView, event: DragEvent, _slice: unknown, moved: boolean): boolean => {
    if (moved) return false;
    const dt = event.dataTransfer;
    if (!dt) return false;
    const editor = opts.editorRef.current;
    if (!editor) return false;

    const dropPos = () =>
      view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
      ?? view.state.selection.from;

    // Composer Block (single reusable text block, optionally with a Signature image)
    // Workspace Media (right rail) → insert by mediaId; node view resolves
    // a fresh signed URL at render time so stored JSON stays a stable ref.
    const mediaRaw = dt.getData("application/x-excerptus-media");
    if (mediaRaw) {
      try {
        const { mediaId, alt } = JSON.parse(mediaRaw) as { mediaId?: string; alt?: string };
        if (mediaId) {
          const pos = dropPos();
          editor.chain().focus().insertContentAt(pos, {
            type: "excerptus.imageCaption",
            attrs: { mediaId, src: null, alt: alt ?? "", caption: null, credit: null },
          }).run();
          event.preventDefault();
          return true;
        }
      } catch {
        return false;
      }
    }

    const blockRaw = dt.getData("application/x-excerptus-block");
    if (blockRaw) {
      try {
        const payload = JSON.parse(blockRaw) as {
          type?: string;
          title?: string;
          body?: string;
          doc?: { type: "doc"; content: unknown[] } | null;
          fragments?: Array<{ kind: "html"; html: string } | { kind: "node"; node: unknown }> | null;
        };
        const pos = dropPos();

        // Preferred path (2026-06-14): ordered fragments preserve rich-text
        // formatting (bold/italic/links/lists/alignment/indent) by passing
        // HTML chunks straight to TipTap's HTML parser.
        if (Array.isArray(payload.fragments) && payload.fragments.length > 0) {
          let cursor = pos;
          for (const frag of payload.fragments) {
            const before = editor.state.doc.content.size;
            if (frag.kind === "html") {
              editor.chain().focus().insertContentAt(cursor, frag.html).run();
            } else {
              editor.chain().focus().insertContentAt(cursor, frag.node as never).run();
            }
            const delta = editor.state.doc.content.size - before;
            cursor += Math.max(0, delta);
          }
          event.preventDefault();
          return true;
        }

        if (payload.doc && Array.isArray(payload.doc.content) && payload.doc.content.length > 0) {
          editor.chain().focus().insertContentAt(pos, payload.doc.content as never).run();
          event.preventDefault();
          return true;
        }
        const body = (payload.body ?? "").trim();
        if (!body) return false;
        if (payload.type === "quote") {
          editor.chain().focus().insertContentAt(pos, {
            type: "blockquote",
            content: [{ type: "paragraph", content: [{ type: "text", text: body }] }],
          }).run();
        } else {
          insertParagraphsFromText(editor, pos, body);
        }
        event.preventDefault();
        return true;
      } catch {
        return false;
      }
    }

    // Composer Blueprint (ordered stack of blocks). text/plain pre-serialized.
    const bpRaw = dt.getData("application/x-excerptus-blueprint");
    if (bpRaw) {
      try {
        const text = dt.getData("text/plain");
        if (!text.trim()) return false;
        insertParagraphsFromText(editor, dropPos(), text);
        event.preventDefault();
        return true;
      } catch {
        return false;
      }
    }

    const excerptRaw = dt.getData("application/x-excerptus-excerpt-ref");
    if (excerptRaw) {
      try {
        const payload = JSON.parse(excerptRaw) as {
          body_html?: string;
          body_text?: string;
          author?: string;
          publication?: string;
          year?: number | null;
          page_start?: number | null;
          page_end?: number | null;
          source_type?: string | null;
          container_title?: string | null;
          accessed_date?: string | null;
          isbn_10?: string | null;
          isbn_13?: string | null;
          timestamp_seconds?: number | null;
          library_item_id?: string | null;
        };
        const rawHtml = payload.body_html?.trim()
          || (payload.body_text ? escapeExcerptText(payload.body_text) : "");
        const cleanBody = sanitizeExcerptBodyHtml(rawHtml);
        if (!cleanBody) return false;
        const pos = dropPos();

        const tag = opts.associatesTagRef.current;
        const isLinkable = payload.source_type === "book" || payload.source_type === "chapter";
        const affiliateHref = isValidAssociatesTag(tag) && isLinkable
          ? buildAmazonAffiliateUrl({
              isbn: payload.isbn_10 ?? payload.isbn_13 ?? null,
              associatesTag: tag,
            })
          : null;

        const citationParts: ExcerptCitationParts = {
          author: payload.author,
          publication: payload.publication,
          year: payload.year ?? null,
          pageStart: payload.page_start ?? null,
          pageEnd: payload.page_end ?? null,
          sourceType: payload.source_type ?? null,
          containerTitle: payload.container_title ?? null,
          accessedDate: payload.accessed_date ?? null,
          timestampSeconds: payload.timestamp_seconds ?? null,
        };

        const excerptLabel = [payload.author, payload.publication].filter(Boolean).join(". ")
          + (payload.year ? ` (${payload.year})` : "");
        if (affiliateHref) {
          // Defer the actual insertion until the user confirms via popover.
          opts.pendingExcerptDropRef.current = {
            pos,
            bodyHtml: cleanBody,
            citationParts,
            affiliateHref,
            libraryItemId: payload.library_item_id ?? null,
            label: excerptLabel,
          };
          opts.setLibDropAnchor({ x: event.clientX, y: event.clientY });
          opts.setLibDropTitle(payload.publication?.trim() || "Excerpt");
          opts.setLibDropOpen(true);
        } else {
          editor.chain().focus()
            .insertContentAt(pos, `<blockquote><p>${cleanBody}</p></blockquote>`)
            .run();
          const citeHtml = buildExcerptCitationHtml(citationParts);
          if (citeHtml) {
            editor.chain().focus().insertContent(citeHtml).run();
          }
          if (payload.library_item_id) opts.onSourceInserted?.(payload.library_item_id, excerptLabel);
        }
        event.preventDefault();
        return true;
      } catch {
        return false;
      }
    }

    const libRaw = dt.getData("application/x-excerptus-library-ref");
    if (!libRaw) return false;
    try {
      const payload = JSON.parse(libRaw) as {
        id?: string;
        title?: string;
        author?: string;
        source_type?: string | null;
        isbn_10?: string | null;
        isbn_13?: string | null;
        published_year?: number | null;
      };
      const source: LibraryQuoteSource = {
        title: payload.title ?? null,
        author: payload.author ?? null,
        source_type: payload.source_type ?? null,
        isbn_10: payload.isbn_10 ?? null,
        isbn_13: payload.isbn_13 ?? null,
        published_year: payload.published_year ?? null,
      };
      const pos = dropPos();
      const libLabel = [payload.author, payload.title].filter(Boolean).join(". ")
        + (payload.published_year ? ` (${payload.published_year})` : "");
      const tag = opts.associatesTagRef.current;
      const { eligible } = resolveAmazonEligibility(source, tag);
      if (!eligible) {
        const ins = buildLibraryQuoteInsert({
          source,
          workspaceAssociatesTag: tag,
          includeAffiliateLink: false,
        });
        editor.chain().focus().insertContentAt(pos, ins.blockquoteNode).run();
        editor.chain().focus().insertContent(ins.attributionHtml).run();
        if (payload.id) opts.onSourceInserted?.(payload.id, libLabel);
      } else {
        // Defer insertion until the user confirms via the popover.
        opts.pendingLibDropRef.current = { source, pos, libraryItemId: payload.id ?? null };
        opts.setLibDropAnchor({ x: event.clientX, y: event.clientY });
        opts.setLibDropTitle(source.title ?? "Library item");
        opts.setLibDropOpen(true);
      }
      event.preventDefault();
      return true;
    } catch {
      return false;
    }
  };
}
